/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useRef, useState } from 'react';
import type videojs from 'video.js';
import * as Sentry from '@sentry/react';
import 'video.js/dist/video-js.css';
import '../../../styles/videojs-city-theme.css';

type VideoJsPlayerInstance = ReturnType<typeof videojs>;

interface VideoJsPlayerProps {
  options: IVideoOptionsProps;
  onReady?: (player: VideoJsPlayerInstance) => void;
}

export function VideoJsPlayer({ options, onReady }: VideoJsPlayerProps) {
  const videoRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<VideoJsPlayerInstance | null>(null);
  const videojsRef = useRef<typeof videojs | null>(null);
  // TELV1-550 — video.js is a lazy chunk (vendor-video) fetched the first
  // time a video stimulus renders. On flaky mobile networks that fetch can
  // fail, and once it has, the browser's module map rejects every later
  // import() of the same URL for the life of the document — a retry cannot
  // recover it. Falling back to the browser's own <video> keeps the stimulus
  // playable instead of leaving the respondent with an empty box.
  const [useNativeFallback, setUseNativeFallback] = useState(false);
  const fallbackRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const initPlayer = async () => {
      if (fallbackRef.current) return;

      if (!videojsRef.current) {
        try {
          const mod = await import('video.js');
          if (cancelled) return;
          videojsRef.current = mod.default;
        } catch (error) {
          if (cancelled) return;
          fallbackRef.current = true;
          Sentry.captureMessage('video.js chunk failed to load — native <video> fallback', {
            level: 'warning',
            tags: { chunk_error: 'true', video_fallback: 'true' },
            extra: { reason: error instanceof Error ? error.message : String(error) },
          });
          // Internal state only: `options` / `onReady` identities are untouched,
          // so this effect does not re-run because of the fallback switch.
          setUseNativeFallback(true);
          return;
        }
      }

      const vjs = videojsRef.current;

      if (!playerRef.current && videoRef.current) {
        const videoElement = document.createElement('video-js');
        videoElement.classList.add('vjs-big-play-centered');
        if (options.controlsList) {
          videoElement.setAttribute('controlsList', options.controlsList);
        }
        videoRef.current.appendChild(videoElement);

        // eslint-disable-next-line no-multi-assign
        const player = (playerRef.current = vjs(videoElement, options, () => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          onReady && onReady(player);
        }));
      } else if (playerRef.current) {
        playerRef.current.autoplay(options.autoplay);
        playerRef.current.src(options.sources);
      }
    };

    initPlayer();

    return () => {
      cancelled = true;
    };
  }, [options, onReady]);

  // Dispose the Video.js player when the component unmounts. The ref is read
  // inside the cleanup on purpose: the player is created asynchronously by the
  // effect above, so at mount time it is still null — capturing it here used to
  // dispose nothing and leaked the media element + listeners of every stimulus.
  useEffect(
    () => () => {
      const player = playerRef.current;
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    },
    [],
  );

  if (useNativeFallback) {
    // Nothing playable to fall back to: rendering a <video> with no <source>
    // would show the browser's broken-player chrome, which is worse than the
    // message bubble on its own. The Sentry warning has already been sent.
    if (!options.sources?.length) return null;

    // Plain wrapper, deliberately without the `video-js` classes so the theme
    // stylesheet does not restyle a player that is not video.js.
    return (
      // No caption track: these are customer-supplied research stimuli and we
      // have no caption files for them. An empty <track src=""> would satisfy
      // the lint rule while providing nothing to a screen reader (and logs a
      // console error on some UAs), so disable the rule instead — the same
      // call the audio/transcript players in this repo already make.
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        controls
        playsInline
        preload="metadata"
        controlsList="nodownload"
        disablePictureInPicture
        className="w-full h-auto bg-black"
        style={{ aspectRatio: '16 / 9' }}
      >
        {options.sources.map((source: { src: string; type: string }) => (
          <source key={source.src} src={source.src} type={source.type} />
        ))}
      </video>
    );
  }

  return (
    <div
      data-vjs-player
      style={{
        objectFit: 'cover',
        aspectRatio: '16 / 9',
        width: '100%',
      }}
      className="w-full h-auto video-js vjs-theme-city vjs-picture-in-picture-control"
    >
      <div ref={videoRef} />
    </div>
  );
}

export default VideoJsPlayer;
